/**
 * The div the widget renders into.
 *
 * App.tsx looks this up first and only appends its own if the host page did not already
 * provide one -- which is what makes embedding possible at all.
 */
export const APP_ELEMENT_ID = "xapp-form-widget";

/**
 * Class the host page puts on its own `#xapp-form-widget` div to opt into inline rendering.
 *
 * ```html
 * <div id="xapp-form-widget" class="embedded" style="width: 400px; height: 600px;"></div>
 * ```
 */
export const EMBEDDED_CLASS = "embedded";

/**
 * Namespaced alias for {@link EMBEDDED_CLASS}.
 *
 * `embedded` is short enough that a host page's own CSS framework may already style it;
 * this gives those customers a class that cannot collide.
 */
export const XAPP_EMBEDDED_CLASS = "xapp-embedded";

/**
 * Is the widget rendering inside a container the host page laid out and sized itself?
 *
 * Embedded, the customer's div is the frame: the widget must fill it rather than open a
 * fixed, full-viewport modal over the page.
 *
 * @param element - the container to inspect; defaults to looking up {@link APP_ELEMENT_ID}.
 */
export function isEmbedded(element?: Element | null): boolean {
    const host =
        element ??
        (typeof document !== "undefined" ? document.getElementById(APP_ELEMENT_ID) : null);

    if (!host) {
        return false;
    }

    // classList, not className.includes -- a customer class of `not-embedded-at-all` must
    // not opt them in.
    return host.classList.contains(EMBEDDED_CLASS) || host.classList.contains(XAPP_EMBEDDED_CLASS);
}
